import React, { useState } from 'react';
import {
  Form,
  FormItem,
  SubmitButton,
  Input,
  Button,
  Dropdown,
  Container,
} from '../../components';
import { ModelType, modelTypesOptions } from './const';
import type { NodeJSON } from '../../api';
import { validateJSONforInput } from '../../utilities';

export type ModelFormProps = {
  type: ModelType;
  modelName: string;
  providerName: string;
  settings: string;
  settingsValidationSchema: string;
  uiSchema: string;
};

type CreateModelFormProps = {
  saving: boolean;
  form?: ModelFormProps | null;
  onCancel: () => void;
  onSave: (form: any) => void;
};

const defaultFormValues: ModelFormProps = {
  type: ModelType.LLM,
  modelName: '',
  providerName: '',
  settings: JSON.stringify({} as NodeJSON, null, 2),
  settingsValidationSchema: JSON.stringify({} as NodeJSON, null, 2),
  uiSchema: JSON.stringify({} as NodeJSON, null, 2),
};

const CreateModelForm: React.FC<CreateModelFormProps> = ({
  saving,
  onSave,
  onCancel,
}) => {
  const [values, setValues] = useState<ModelFormProps>(defaultFormValues);

  const handleSave = () => {
    try {
      const payload = {
        type: values.type,
        modelName: values.modelName,
        providerName: values.providerName,
        settings: JSON.parse(values.settings),
        settingsValidationSchema: JSON.parse(values.settingsValidationSchema),
        uiSchema: JSON.parse(values.uiSchema),
      };

      onSave(payload);
    } catch (error) {
      console.error('Invalid JSON fields', error);
      alert('Please correct invalid JSON before submitting.');
    }
  };

  return (
    <div className="relative h-full flex flex-col overflow-hidden">
      <Form onChange={setValues} initialValue={values}>
        <div className="flex-1 overflow-y-auto pr-1">
          <Container>
            <FormItem<ModelFormProps>
              required
              name="type"
              label="Model Type"
              inline={false}
            >
              <Dropdown
                placeholder="Select Model Type"
                options={modelTypesOptions}
              />
            </FormItem>
            <FormItem<ModelFormProps>
              name="modelName"
              label="Model Name"
              inline={false}
              required
            >
              <Input placeholder='e.g. "gpt-3.5-turbo"' />
            </FormItem>
            <FormItem<ModelFormProps>
              name="providerName"
              label="Provider Name"
              inline={false}
              required
            >
              <Input placeholder="e.g. OpenAI" />
            </FormItem>

            <FormItem<ModelFormProps>
              name="settings"
              label="Settings JSON"
              required
              validate={validateJSONforInput}
            >
              <Input multiline placeholder="Settings JSON" />
            </FormItem>
            <FormItem<ModelFormProps>
              name="settingsValidationSchema"
              label="Settings Validation Schema JSON"
              required
              validate={validateJSONforInput}
            >
              <Input multiline placeholder="Settings Validation Schema JSON" />
            </FormItem>
            <FormItem<ModelFormProps>
              name="uiSchema"
              label="UI Schema JSON"
              required
              validate={validateJSONforInput}
            >
              <Input multiline placeholder="UI Schema JSON" />
            </FormItem>
          </Container>
        </div>

        <div className="sticky bottom-0 left-0 bg-white  p-md z-10 flex flex-col sm:flex-row justify-end gap-x-md gap-y-md">
          <Button
            size="medium"
            type="contrast"
            label="Cancel"
            className="sm:w-auto w-full text-main"
            onClick={onCancel}
          />
          <SubmitButton
            size="medium"
            disabled={saving}
            className="sm:w-auto w-full"
            label="Create"
            onClick={handleSave}
          />
        </div>
      </Form>
    </div>
  );
};

export default CreateModelForm;
